import React, { useState, useRef, useEffect } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Camera, Save, Wallet, Webhook, Download, ShieldCheck, Volume2, VolumeX, Megaphone, RotateCcw, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import { isPlyxChimeMuted, setPlyxChimeMuted, playPlyxChime } from "@/lib/plyxChime";
import { PLYX_BUILD } from "@/lib/buildInfo";

const PlyxSettings = () => {
  const { user, loading, refresh } = usePlyxAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [form, setForm] = useState({ display_name: "", bio: "", country: "" });
  const { canInstall, installed, isIos, promptInstall } = useInstallPrompt();
  const [chimeMuted, setChimeMuted] = useState(isPlyxChimeMuted());

  useEffect(() => {
    if (user) setForm({
      display_name: user.display_name || "",
      bio: user.bio || "",
      country: user.country || "",
    });
  }, [user]);

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!user) {
    return <Navigate to="/plyx/login" replace state={{ from: "/plyx/settings" }}/>;
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await plyxApi.patch("/plyx/me", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSavingProfile(false);
    }
  };

  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("avatar", f);
      await plyxApi.post("/plyx/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-settings-page">
      <div className="container-narrow py-10 max-w-2xl">
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">Edit profile</h1>
        <p className="text-white/60 mb-8">@{user.handle}</p>

        {/* Avatar */}
        <div className="flex items-center gap-5 p-5 rounded-2xl bg-white/5 border border-white/10 mb-6">
          <div className="w-20 h-20 rounded-full bg-white/10 overflow-hidden flex items-center justify-center text-2xl font-display shrink-0">
            {user.avatar_url
              ? <img src={plyxMediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover"/>
              : user.display_name?.slice(0, 1) || "?"}
          </div>
          <div className="flex-1">
            <div className="font-semibold mb-1">Profile picture</div>
            <div className="text-xs text-white/50 mb-2">JPG or PNG, max 5MB</div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              data-testid="plyx-avatar-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: "#E85D4E" }}
            >
              {uploadingAvatar ? <Loader2 className="w-4 h-4 animate-spin"/> : <Camera className="w-4 h-4"/>}
              {uploadingAvatar ? "Uploading…" : "Change photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarPick} data-testid="plyx-avatar-file-input"/>
          </div>
        </div>

        {/* Profile fields */}
        <form onSubmit={saveProfile} className="space-y-4 p-5 rounded-2xl bg-white/5 border border-white/10">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Display name</label>
            <input
              maxLength={60}
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              data-testid="plyx-settings-display-name"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Bio</label>
            <textarea
              rows={3}
              maxLength={300}
              value={form.bio}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              data-testid="plyx-settings-bio"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
            <div className="text-xs text-white/40 mt-1">{form.bio.length}/300</div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Country</label>
            <input
              maxLength={60}
              value={form.country}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              data-testid="plyx-settings-country"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <button
            type="submit"
            disabled={savingProfile}
            data-testid="plyx-settings-save"
            className="w-full py-3 rounded-full font-semibold text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ background: "#E85D4E" }}
          >
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            Save profile
          </button>
        </form>

        {/* Wallet entry */}
        <Link
          to="/plyx/wallet"
          data-testid="plyx-settings-wallet-link"
          className="mt-6 flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-[#FFB42D]"/>
            <div>
              <div className="font-semibold">Watch-to-earn wallet</div>
              <div className="text-xs text-white/50">See your Plyx credits</div>
            </div>
          </div>
          <span className="text-white/40">›</span>
        </Link>

        {/* Webhooks entry */}
        <Link
          to="/plyx/settings/webhooks"
          data-testid="plyx-settings-webhooks-link"
          className="mt-3 flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Webhook className="w-5 h-5 text-white/85"/>
            <div>
              <div className="font-semibold">Discord, Telegram &amp; Slack webhooks</div>
              <div className="text-xs text-white/50">Ping your community when you go live or receive a big tip</div>
            </div>
          </div>
          <span className="text-white/40">›</span>
        </Link>

        {/* Sell on Plyx — seller verification + Stripe Connect onboarding. */}
        <Link
          to="/plyx/seller"
          data-testid="plyx-settings-seller-link"
          className="mt-3 flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-[#FF2D7A]"/>
            <div>
              <div className="font-semibold">Sell on Plyx</div>
              <div className="text-xs text-white/50">Verify your identity &amp; connect Stripe to start selling</div>
            </div>
          </div>
          <span className="text-white/40">›</span>
        </Link>

        {/* Promo kit — branded PNG posters + QR codes for off-platform
            sharing (WhatsApp Status, IG Story, Twitter, flyers, …). */}
        <Link
          to="/plyx/promo"
          data-testid="plyx-settings-promo-link"
          className="mt-3 flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Megaphone className="w-5 h-5 text-[#FFB42D]"/>
            <div>
              <div className="font-semibold">Plyx promo kit</div>
              <div className="text-xs text-white/50">Branded posters &amp; QR codes — share Plyx anywhere</div>
            </div>
          </div>
          <span className="text-white/40">›</span>
        </Link>

        {/* Restore-broken — emergency lever after the heal-sweep
            regression marked legitimate clips broken because YouTube
            returned 4xx for HEAD probes. One tap flips every
            `status="broken"` upload on this account back to
            `published`. Idempotent — safe to tap any time. */}
        <button
          type="button"
          data-testid="plyx-settings-restore-broken"
          onClick={async () => {
            try {
              const r = await plyxApi.post("/plyx/admin/restore-broken?scope=mine");
              const n = r?.data?.restored ?? 0;
              toast.success(
                n === 0
                  ? "No broken videos to restore — you're all good ✓"
                  : `Restored ${n} video${n === 1 ? "" : "s"} to your gallery`,
              );
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Restore failed");
            }
          }}
          className="mt-3 w-full flex items-center justify-between p-5 rounded-2xl bg-amber-300/10 border border-amber-300/30 hover:bg-amber-300/15 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <RotateCcw className="w-5 h-5 text-amber-300" />
            <div>
              <div className="font-semibold">Restore my broken videos</div>
              <div className="text-xs text-white/60">
                Brings back any of your clips the heal sweep wrongly flagged
              </div>
            </div>
          </div>
          <span className="text-amber-300">↺</span>
        </button>

        {/* Regenerate thumbnails — fix the "black/blank gallery"
            issue by re-extracting a JPG frame for every clip the
            caller owns that doesn't already have one. Idempotent. */}
        <button
          type="button"
          data-testid="plyx-settings-regen-thumbs"
          onClick={async () => {
            const t = toast.loading("Regenerating thumbnails… this may take 30-60s");
            try {
              const r = await plyxApi.post("/plyx/me/videos/regenerate-thumbnails");
              const { scanned = 0, healed = 0, skipped = 0, failed = 0 } = r?.data || {};
              toast.dismiss(t);
              if (healed > 0) {
                toast.success(
                  `Regenerated ${healed} thumbnail${healed === 1 ? "" : "s"} · ${skipped} already healthy`,
                );
              } else if (scanned === 0) {
                toast.info("You haven't uploaded any native videos yet");
              } else {
                toast.info(`All ${skipped} videos already have thumbnails ✓`);
              }
              if (failed > 0) {
                toast.warning(`${failed} clip${failed === 1 ? "" : "s"} couldn't be processed — try again later`);
              }
            } catch (err) {
              toast.dismiss(t);
              toast.error(err?.response?.data?.detail || "Regenerate failed");
            }
          }}
          className="mt-3 w-full flex items-center justify-between p-5 rounded-2xl bg-cyan-300/10 border border-cyan-300/30 hover:bg-cyan-300/15 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <ImageIcon className="w-5 h-5 text-cyan-300" />
            <div>
              <div className="font-semibold">Fix blank gallery tiles</div>
              <div className="text-xs text-white/60">
                Re-extract JPG thumbnails for any of your videos showing as black
              </div>
            </div>
          </div>
          <span className="text-cyan-300">↺</span>
        </button>

        {/* Plyx chime mute toggle — the brand "ding" plays at micro-
            moments (uploads, purchases, install). Some users prefer
            silence; this is the per-device opt-out. */}
        <button
          type="button"
          data-testid="plyx-settings-chime-toggle"
          onClick={() => {
            const next = !chimeMuted;
            setPlyxChimeMuted(next);
            setChimeMuted(next);
            // Sound the chime when un-muting so the user hears what
            // they're enabling. Use force=true to bypass the freshly
            // updated mute state on the same tick.
            if (!next) playPlyxChime({ force: true });
          }}
          className="mt-3 w-full flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            {chimeMuted
              ? <VolumeX className="w-5 h-5 text-white/40"/>
              : <Volume2 className="w-5 h-5 text-[#FFB42D]"/>}
            <div>
              <div className="font-semibold">Plyx chime</div>
              <div className="text-xs text-white/50">
                {chimeMuted
                  ? "Off — tap to enable the brand ding at uploads &amp; purchases"
                  : "On — short ding at upload, purchase &amp; install moments"}
              </div>
            </div>
          </div>
          <span className="text-white/40">{chimeMuted ? "off" : "on"}</span>
        </button>

        {/* Install Plyx to home screen — always visible so the user can
            re-install even after dismissing the auto-prompt. Renders a
            helpful state message when already installed or when the
            current browser doesn't support PWA install (e.g. Firefox iOS). */}
        <button
          type="button"
          data-testid="plyx-settings-install-app"
          onClick={async () => {
            if (installed) { toast("Plyx is already installed on this device", { icon: "✅" }); return; }
            const r = await promptInstall();
            if (r === "ios") {
              // Surface the iOS instructions modal via the global event.
              window.dispatchEvent(new Event("plyx-show-install"));
            } else if (r === "unavailable") {
              toast("Your browser doesn't support install yet — try Chrome or Safari.", { duration: 4000 });
            } else if (r === "installed") {
              toast.success("Plyx installed! Check your home screen.");
            }
          }}
          className="w-full mt-3 flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "#E85D4E" }}
            >
              <Download className="w-4 h-4 text-black" strokeWidth={2.4}/>
            </div>
            <div>
              <div className="font-semibold">
                {installed ? "Plyx is installed" : "Install Plyx on your phone"}
              </div>
              <div className="text-xs text-white/50">
                {installed
                  ? "You're running the installed app — nothing to do."
                  : isIos
                    ? "Tap to see Safari Add-to-Home-Screen instructions"
                    : canInstall
                      ? "One-tap install, no app store needed"
                      : "Open this page in Chrome or Safari to install"}
              </div>
            </div>
          </div>
          <span className="text-white/40">›</span>
        </button>

        <button
          type="button"
          onClick={() => navigate(`/plyx/u/${user.handle}`)}
          data-testid="plyx-settings-view-profile"
          className="mt-6 text-sm text-white/60 hover:text-white underline"
        >
          ← Back to my profile
        </button>
        <div className="mt-4 text-[10px] text-white/30" data-testid="plyx-settings-build">App build: {PLYX_BUILD}</div>
      </div>
    </div>
  );
};

export default PlyxSettings;
